import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useRef } from "react";
import { Toaster } from "./components/ui/sonner";

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://your-railway-backend-url.railway.app'}/api`;

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${storedToken}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(storedToken);
      } else {
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (newToken, userData, profileNeeded = false) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setNeedsProfileSetup(profileNeeded || !userData?.role);
  };

  const completeProfileSetup = () => {
    setNeedsProfileSetup(false);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, checkAuth, needsProfileSetup, completeProfileSetup }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading, needsProfileSetup } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to profile setup if needed
  if (needsProfileSetup && location.pathname !== '/profile-setup') {
    return <Navigate to="/profile-setup" state={{ from: location }} replace />;
  }

  return children;
}

import { CreditProvider } from "./contexts/CreditContext";
import UpgradeModal from "./components/UpgradeModal";

// Lazy load pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import ProjectWorkspace from "./pages/ProjectWorkspace";
import ProfileSetup from "./pages/ProfileSetup";
import Pricing from "./pages/Pricing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import PortfolioReporting from "./pages/PortfolioReporting";
import ProjectTemplates from "./pages/ProjectTemplates";
import HelpCenter from "./pages/HelpCenter";
import BuyCredits from "./pages/BuyCredits";
import PricingIntelligence from "./pages/PricingIntelligence";
import ProfileSettings from "./pages/ProfileSettings";
import HelpButton from "./components/HelpButton";

// App Router
function AppRouter() {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/profile-setup"
        element={
          <ProtectedRoute>
            <ProfileSetupRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId"
        element={
          <ProtectedRoute>
            <ProjectWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId/:phase"
        element={
          <ProtectedRoute>
            <ProjectWorkspace />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <PortfolioReporting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <ProjectTemplates />
          </ProtectedRoute>
        }
      />
      <Route path="/help" element={<HelpCenter />} />
      <Route
        path="/buy-credits"
        element={
          <ProtectedRoute>
            <BuyCredits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pricing-intelligence"
        element={
          <ProtectedRoute>
            <PricingIntelligence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// Profile Setup Route Component
function ProfileSetupRoute() {
  const navigate = useNavigate();
  const { completeProfileSetup } = useAuth();
  
  const handleComplete = () => {
    completeProfileSetup();
    navigate('/dashboard');
  };
  
  return <ProfileSetup onComplete={handleComplete} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CreditBridge>
          <AppRouter />
          <HelpButton />
          <UpgradeModal />
          <Toaster position="top-right" richColors />
        </CreditBridge>
      </AuthProvider>
    </BrowserRouter>
  );
}

/** Bridge: reads token from AuthContext, passes to CreditProvider. */
function CreditBridge({ children }) {
  const { token } = useAuth();
  return <CreditProvider token={token}>{children}</CreditProvider>;
}

export default App;
